import { motion } from 'framer-motion';
import {
  CheckSquare,
  Briefcase,
  Clock,
  CalendarDays,
  GraduationCap,
  BarChart3,
} from 'lucide-react';

const features = [
  {
    icon: CheckSquare,
    title: 'Smart Task Management',
    description: 'Organize tasks with Kanban boards, priorities, categories, and calendar views.',
    gradient: 'from-primary/10 to-primary-glow/10',
    iconBg: 'gradient-primary',
    glow: 'group-hover:shadow-[0_0_40px_-10px_hsl(var(--primary)/0.3)]',
  },
  {
    icon: Briefcase,
    title: 'Job Application Tracker',
    description: 'Track your entire job search pipeline from application to offer.',
    gradient: 'from-tasks-upskilling/10 to-primary/10',
    iconBg: 'gradient-purple',
    glow: 'group-hover:shadow-[0_0_40px_-10px_hsl(270,70%,55%,0.3)]',
  },
  {
    icon: Clock,
    title: 'Work Activity Logger',
    description: 'Log daily work hours by project and category with rich analytics.',
    gradient: 'from-success/10 to-info/10',
    iconBg: 'gradient-success',
    glow: 'group-hover:shadow-[0_0_40px_-10px_hsl(var(--success)/0.3)]',
  },
  {
    icon: CalendarDays,
    title: 'Meeting Scheduler',
    description: 'Manage meetings, agendas, and notes with calendar integration.',
    gradient: 'from-warning/10 to-destructive/10',
    iconBg: 'gradient-warning',
    glow: 'group-hover:shadow-[0_0_40px_-10px_hsl(var(--warning)/0.3)]',
  },
  {
    icon: GraduationCap,
    title: 'Skills Progress Tracker',
    description: 'Plan your upskilling journey with progress tracking and resource management.',
    gradient: 'from-info/10 to-success/10',
    iconBg: 'bg-info',
    glow: 'group-hover:shadow-[0_0_40px_-10px_hsl(var(--info)/0.3)]',
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description: 'Visualize your productivity with charts, trends, and actionable insights.',
    gradient: 'from-destructive/10 to-warning/10',
    iconBg: 'gradient-danger',
    glow: 'group-hover:shadow-[0_0_40px_-10px_hsl(var(--destructive)/0.3)]',
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] } },
};

export function FeaturesSection() {
  return (
    <section className="px-4 py-28 sm:px-6 lg:px-8 relative" id="features">
      <div className="pointer-events-none absolute inset-0 mesh-gradient opacity-50" />
      {/* Floating grid */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(to_right,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      
      <div className="mx-auto max-w-6xl relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block mb-4 text-xs font-semibold uppercase tracking-widest text-primary"
          >
            Powerful Features
          </motion.span>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Everything you need to{' '}
            <span className="text-gradient">stay productive</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-muted-foreground text-lg leading-relaxed">
            Six powerful modules working together to help you manage your professional life.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-50px' }}
          className="mt-20 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              variants={item}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className={`group relative rounded-2xl border border-border/40 bg-gradient-to-br ${feature.gradient} p-7 transition-all duration-500 glass-subtle ${feature.glow} overflow-hidden`}
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary/[0.03] to-transparent" />
              
              <div className="relative z-10">
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                  className={`inline-flex rounded-2xl p-3.5 ${feature.iconBg} shadow-lg`}
                >
                  <feature.icon className="h-5 w-5 text-white" />
                </motion.div>
                <h3 className="mt-5 font-display text-lg font-bold text-card-foreground">
                  {feature.title}
                </h3>
                <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
